import React, { useState, useMemo } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Clients can self-book from tomorrow onwards, never same-day (must match backend /timeslots/available)
const filterFutureSlots = (slots) => {
  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  return slots.filter(slot => slot.date > todayStr);
};

const SessionBooking = ({ availableSlots, onBookSession, t }) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [notes, setNotes] = useState('');
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Filter slots to only show future slots (safety net - backend should already filter)
  const futureSlots = useMemo(() => filterFutureSlots(availableSlots), [availableSlots]);

  // Group slots by date
  const slotsByDate = futureSlots.reduce((acc, slot) => {
    if (!acc[slot.date]) {
      acc[slot.date] = [];
    }
    acc[slot.date].push(slot);
    return acc;
  }, {});

  // Sort slots by start time for selected date
  const getTimeSlotsForDate = (date) => {
    if (!slotsByDate[date]) return [];
    return slotsByDate[date].sort((a, b) => a.start_time.localeCompare(b.start_time));
  };

  // Generate proper calendar grid for the month
  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    // First day of the month
    const firstDay = new Date(year, month, 1);
    // Last day of the month
    const lastDay = new Date(year, month + 1, 0);
    
    // Get the day of week for the first day (0 = Sunday, 1 = Monday, etc.)
    // We want Monday as first day, so adjust: (day + 6) % 7 makes Monday = 0
    const firstDayOfWeek = (firstDay.getDay() + 6) % 7;
    
    const days = [];
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    
    // Add empty cells for days before the first of the month
    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push({ empty: true, key: `empty-start-${i}` });
    }
    
    // Add days of the month
    for (let day = 1; day <= lastDay.getDate(); day++) {
      const date = new Date(year, month, day);
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dayOfWeek = date.getDay();
      const isSunday = dayOfWeek === 0;
      const isPast = dateStr < todayStr;
      const hasSlots = slotsByDate[dateStr] && slotsByDate[dateStr].length > 0;
      
      days.push({
        empty: false,
        date: dateStr,
        day: day,
        dayName: date.toLocaleDateString('es-ES', { weekday: 'short' }),
        hasSlots: hasSlots && !isSunday && !isPast,
        isToday: dateStr === todayStr,
        isSunday,
        isPast,
        key: dateStr
      });
    }
    
    return days;
  };

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const handleBooking = () => {
    if (selectedSlot) {
      onBookSession(selectedSlot.id, notes);
      setSelectedSlot(null);
      setSelectedDate(null);
      setNotes('');
    }
  };

  const calendarDays = generateCalendarDays();

  return (
    <div>
      <h2 className="text-xl font-bold text-white mb-6">Reservar una sesión</h2>
      
      {/* Calendar View */}
      <div className="glass-card p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <button onClick={prevMonth} className="text-blue-300 hover:text-white p-2">◀</button>
          <h3 className="text-lg font-semibold text-white">
            {currentMonth.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
          </h3>
          <button onClick={nextMonth} className="text-blue-300 hover:text-white p-2">▶</button>
        </div>
        
        {/* Calendar Grid - 7 columns (Mon-Sun) */}
        <div className="grid grid-cols-7 gap-1 mb-4">
          {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map(day => (
            <div key={day} className="text-center text-blue-300 font-medium py-2 text-sm">
              {day}
            </div>
          ))}
        </div>
        
        <div className="grid grid-cols-7 gap-1">
          {calendarDays.map((calDay) => {
            if (calDay.empty) {
              return <div key={calDay.key} className="h-12"></div>;
            }
            
            const isDisabled = calDay.isSunday || calDay.isPast || !calDay.hasSlots;
            const isSelected = selectedDate === calDay.date;
            
            return (
              <button
                key={calDay.key}
                onClick={() => !isDisabled && setSelectedDate(calDay.date)}
                disabled={isDisabled}
                className={`h-12 rounded-lg text-sm font-medium transition-all ${
                  calDay.isSunday
                    ? 'bg-gray-800/30 text-gray-600 cursor-not-allowed'
                    : calDay.isPast
                      ? 'bg-gray-700/20 text-gray-500 cursor-not-allowed'
                      : calDay.hasSlots
                        ? isSelected
                          ? 'bg-blue-500 text-white'
                          : 'bg-blue-900/30 text-blue-200 hover:bg-blue-800/40'
                        : 'bg-gray-600/20 text-gray-500 cursor-not-allowed'
                } ${
                  calDay.isToday ? 'ring-2 ring-yellow-400' : ''
                }`}
              >
                <div className="text-center">
                  <div className="font-bold">{calDay.day}</div>
                  {calDay.hasSlots && (
                    <div className="text-xs opacity-80">
                      {slotsByDate[calDay.date]?.length || 0}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
        
        <div className="flex items-center flex-wrap gap-4 mt-4 text-sm">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-blue-500 rounded"></div>
            <span className="text-blue-200">Disponible</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-gray-600/20 rounded"></div>
            <span className="text-gray-500">Sin huecos</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 border-2 border-yellow-400 rounded"></div>
            <span className="text-blue-200">Hoy</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-gray-800/30 rounded"></div>
            <span className="text-gray-500">Cerrado (Dom)</span>
          </div>
        </div>
      </div>

      {/* Time Slots for Selected Date */}
      {selectedDate && (
        <div className="glass-card p-6 mb-6">
          <h3 className="text-lg font-semibold text-white mb-4">
            Horarios disponibles - {new Date(selectedDate + 'T12:00:00').toLocaleDateString('es-ES', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {getTimeSlotsForDate(selectedDate).map((slot) => (
              <button
                key={slot.id}
                onClick={() => setSelectedSlot(slot)}
                className={`p-4 rounded-lg text-left transition-all ${
                  selectedSlot?.id === slot.id
                    ? 'bg-green-500/30 border-2 border-green-400 text-white'
                    : 'bg-blue-900/20 border border-blue-400/30 text-blue-200 hover:bg-blue-800/30'
                }`}
              >
                <div className="font-semibold text-lg">
                  {slot.start_time}
                </div>
                <div className="text-sm opacity-80">
                  hasta {slot.end_time}
                </div>
                <div className="text-xs mt-2 flex items-center space-x-1">
                  <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  <span>{slot.max_clients - slot.current_clients} plazas disponibles</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Booking Confirmation */}
      {selectedSlot && (
        <div className="glass-card p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Confirmar tu reserva</h3>
          <div className="bg-blue-900/20 p-4 rounded-lg mb-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-blue-300 text-sm">Fecha</div>
                <div className="text-white font-semibold">
                  {new Date(selectedSlot.date + 'T12:00:00').toLocaleDateString('es-ES', { 
                    weekday: 'short',
                    month: 'short', 
                    day: 'numeric' 
                  })}
                </div>
              </div>
              <div>
                <div className="text-blue-300 text-sm">Hora</div>
                <div className="text-white font-semibold">
                  {selectedSlot.start_time} - {selectedSlot.end_time}
                </div>
              </div>
              <div>
                <div className="text-blue-300 text-sm">Duración</div>
                <div className="text-white font-semibold">60 min</div>
              </div>
            </div>
          </div>
          
          <div className="mb-6">
            <label className="block text-blue-200 text-sm font-medium mb-2">
              Notas para el entrenador (opcional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="input-field"
              rows="3"
              placeholder="Cualquier objetivo específico o nota para tu entrenador..."
            />
          </div>
          
          <div className="flex space-x-4">
            <button onClick={handleBooking} className="btn-primary flex-1">
              Confirmar reserva
            </button>
            <button 
              onClick={() => {
                setSelectedSlot(null);
                setSelectedDate(null);
              }} 
              className="btn-secondary"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Empty State */}
      {availableSlots.length === 0 && (
        <div className="glass-card p-8 text-center">
          <h3 className="text-lg font-semibold text-blue-200 mb-2">No hay huecos disponibles</h3>
          <p className="text-blue-300">
            Tu entrenador aún no ha creado horarios disponibles. 
            Por favor, contacta con él para programar tu sesión.
          </p>
        </div>
      )}
    </div>
  );
};

export default SessionBooking;
